import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { maxWidthContainer } from '@noths/polaris-client-ribbons-base';
import { NotificationInfo, PreviewHeader } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import type { BreadcrumbLink } from '@noths/polaris-design-system-candidate-components';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { BackToTopButton } from 'src/components/molecules/BackToTopButton/BackToTopButton';
import { BreadcrumbsContainer } from 'src/components/molecules/Breadcrumbs/BreadcrumbsContainer';
import { CopyBlockContainer } from 'src/components/molecules/CopyBlock/CopyBlockContainer';
import { MembershipBannerSection } from 'src/components/molecules/MembershipBannerSection/MembershipBannerSection';
import { ProductListError } from 'src/components/molecules/ProductListError/ProductListError';
import { HeaderCopyBlockContainer } from 'src/components/organisms/HeaderCopyBlock/HeaderCopyBlockContainer';
import type { LinkCarouselContainerProps } from 'src/components/organisms/LinkCarousel/LinkCarouselContainer';
import {
  LinkCarouselContainer,
  LinkCarouselTrackingCategory,
} from 'src/components/organisms/LinkCarousel/LinkCarouselContainer';
import { ListingsControlsDrawerContainer } from 'src/components/organisms/ListingsControlsDrawer/ListingsControlsDrawerContainer';
import { RELATED_CATEGORIES_TITLE } from 'src/components/organisms/ProductPages/constants/copy';
import { useFilterNavType } from 'src/hooks/useFilterNavType/useFilterNavType';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import type { GetLDItemListSchemaArgs } from 'src/seo/linkedData/getLDItemListSchema';
import * as styles from 'src/styles/pages.styles';
import type { NextConfig } from 'src/types/nextConfig';
import type { PageProps } from 'src/types/page';
import { ListingsLayout } from '../ListingLayout/ListingsLayout';

const {
  publicRuntimeConfig: { CLEAR_PREVIEW_API_URL },
} = getConfig() as NextConfig;

export type CategoryListingProps = PageProps & {
  bottomLinks: LinkCarouselContainerProps['links'];
  breadcrumbLinks: BreadcrumbLink[];
  hideRecentlyViewedPlacement: boolean;
  linkedDataSchema?: GetLDItemListSchemaArgs;
  pageLiveRegionText: string;
  pageTitle: string;
  paginationTagValues: (string | null)[];
  preview: boolean;
  stickyTopBanner?: boolean;
  topLinks: LinkCarouselContainerProps['links'];
};

export const CategoryListing = ({
  bottomLinks,
  breadcrumbLinks = [],
  pageLiveRegionText,
  preview,
  showProductsList,
  stickyTopBanner = false,
  topLinks,
  updatedReduxState,
}: CategoryListingProps) => {
  const { query } = useRouter();
  // useDispatch rather than useStore(): server-side getStore() always builds a
  // fresh store, so calling it here constructed an entire throwaway store per
  // SSR render just to reach dispatch for the client-only effect below.
  const dispatch = useDispatch();
  const { products } = updatedReduxState as ReduxApplicationState;
  const [redirectNotification, setRedirectNotification] = useState(false);

  useFilterNavType((filterNavType) =>
    dispatch(navigationActions.setInitialPageLoad(filterNavType)),
  );

  useEffect(() => {
    if (query.redirectedFrom === 'pdp') {
      setRedirectNotification(true);
    }
  }, [query.redirectedFrom]);

  return (
    <>
      {preview && <PreviewHeader href={CLEAR_PREVIEW_API_URL} />}
      <div css={visuallyHidden} role="status">
        {pageLiveRegionText}
      </div>
      <div css={styles.wrapper}>
        <div css={[styles.grid, maxWidthContainer]}>
          <div css={styles.breadcrumbsContentWrapper}>
            <BreadcrumbsContainer breadcrumbsLinks={breadcrumbLinks} />
          </div>
        </div>
        <div css={[styles.grid, maxWidthContainer]}>
          <div css={styles.headerContentWrapper}>
            {redirectNotification && (
              <NotificationInfo>
                We don&apos;t seem to have that product anymore, but here are some similar items
                below.
              </NotificationInfo>
            )}
            <HeaderCopyBlockContainer />
            {topLinks.length > 0 && (
              <section css={styles.linksCarouselSection}>
                <LinkCarouselContainer
                  links={topLinks}
                  trackingCategory={LinkCarouselTrackingCategory.ChildCategory}
                />
              </section>
            )}
          </div>
        </div>
        {showProductsList ? (
          <ListingsLayout stickyTopBanner={stickyTopBanner}>
            {bottomLinks.length > 0 && (
              <div css={styles.relatedCategories}>
                <LinkCarouselContainer
                  links={bottomLinks}
                  title={RELATED_CATEGORIES_TITLE}
                  trackingCategory={LinkCarouselTrackingCategory.RelatedCategory}
                />
              </div>
            )}
            <CopyBlockContainer />
            <MembershipBannerSection trackingLocation="category" />
            <BackToTopButton />
            <ListingsControlsDrawerContainer />
          </ListingsLayout>
        ) : (
          <ProductListError
            message={products.error ? products.error.message : 'No products found'}
          />
        )}
      </div>
    </>
  );
};
